import { OTPData } from "@stores/otpStore"
import { api } from "./authService"

export enum OTPChannel {
	EMAIL_MAGIC_LINK = "email_magic_link",
	EMAIL_OTP = "email_otp",
	SMS = "sms"
}

export enum OTPPurpose {
	REGISTER_AGENT_VERIFICATION = "register_agent_verification",
	REGISTER_AGENCY_VERIFICATION = "register_agency_verification",
	PHONE_VERIFICATION = "phone_verification",
	FORGOT_PASSWORD = "forgot_password",
	FORGOT_USERNAME = "forgot_username",
	EMAIL_VERIFICATION_OTP = "email_verification_otp"
}

interface OTPMetadata {
	userId?: string
	appVersion?: string
	[key: string]: unknown
}

export interface GenerateOTPRequest {
	recipient: string
	channel: OTPChannel
	purpose: OTPPurpose
	metadata?: OTPMetadata
	emailToken?: string
}

/** Payload for `POST /otp/generate-secured`. */
export interface GenerateSecuredOTPRequest extends GenerateOTPRequest {}

export interface GenerateOTPResponse {
	success: boolean
	message?: string
	data?: {
		success: boolean
		token?: string
	}
}

export interface ValidateOTPRequest {
	token: string
	otp?: string
}

export interface ValidateOTPResponse {
	success: boolean
	message?: string
	data?: {
		success: boolean
		token?: string
		metadata?: OTPData
	}
}

export interface AgentEmailVerificationRequest {
	email: string
	agencyPhoneNumber: string
	agencyAbtaNumber: string
	languageCode: string
}

export interface AgencyEmailVerificationRequest {
	email: string
	country: string
}

export const otpService = {
	async generateOTP({
		recipient,
		channel,
		purpose,
		metadata = {},
		emailToken
	}: GenerateOTPRequest): Promise<GenerateOTPResponse> {
		try {
			const { data } = await api.post("/otp/generate", {
				recipient,
				channel,
				purpose,
				metadata,
				...(emailToken && { emailToken })
			})
			return data
		} catch (error) {
			console.error("Error generating OTP:", error)
			throw error
		}
	},
	async sendRegisterAgentEmailVerification(
		email: string,
		agencyPhoneNumber: string,
		agencyAbtaNumber: string,
		languageCode: string
	): Promise<GenerateOTPResponse> {
		const { data } = await api.post("/agents/agent-email-verification", {
			email,
			agencyPhoneNumber,
			agencyAbtaNumber,
			languageCode
		})
		return data
	},

	async sendRegisterAgencyEmailVerification(
		email: string,
		country: string
	): Promise<GenerateOTPResponse> {
		return otpService.generateOTP({
			recipient: email,
			channel: OTPChannel.EMAIL_MAGIC_LINK,
			purpose: OTPPurpose.REGISTER_AGENCY_VERIFICATION,
			metadata: {
				country
			}
		})
	},

	async sendPhoneVerification(
		phoneNumber: string,
		emailToken?: string
	): Promise<GenerateOTPResponse> {
		return otpService.generateOTP({
			recipient: phoneNumber,
			channel: OTPChannel.SMS,
			purpose: OTPPurpose.PHONE_VERIFICATION,
			emailToken
		})
	},

	/** SMS OTP via `/otp/generate-secured` using auth headers from Axios interceptors. */
	async sendPhoneVerificationSecured(
		phoneNumber: string
	): Promise<GenerateOTPResponse> {
		try {
			const body: GenerateSecuredOTPRequest = {
				recipient: phoneNumber,
				channel: OTPChannel.SMS,
				purpose: OTPPurpose.PHONE_VERIFICATION,
				metadata: {}
			}
			const { data } = await api.post("/otp/generate-secured", body)
			return data
		} catch (error) {
			console.error("Error generating secured OTP:", error)
			throw error
		}
	},

	async sendEmailVerificationOTP(email: string): Promise<GenerateOTPResponse> {
		return this.generateOTP({
			recipient: email,
			channel: OTPChannel.EMAIL_OTP,
			purpose: OTPPurpose.EMAIL_VERIFICATION_OTP
		})
	},

	async validateOTP(
		token: string,
		otp?: string,
		emailToken?: string
	): Promise<ValidateOTPResponse> {
		const { data } = await api.post("/otp/validate", {
			token,
			otp,
			...(emailToken && { emailToken })
		})
		return data
	}
}
